import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PhoneCall, MessageSquareText, Mail, Sparkles, CalendarClock, CheckCircle2, ShieldAlert, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { canonicalCallGate, canonicalEmailGate, canonicalPhoneCorrectionGate } from "@/config/followupActionGates.mjs";
import { canonicalSophiaCallGate, canonicalSophiaOpenGate } from "@/config/sophiaCallGate.mjs";
import { SophiaLiveCallPanel } from "@/components/SophiaLiveCallPanel";

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const COMPLETE_OUTCOMES = [
  "Resolved",
  "Policy / service confirmed",
  "Client declined further follow-up",
  "No action needed",
];

function suggestedText(row, lead, agentName) {
  const first = String(row?.client_name || lead?.client_name || "there").trim().split(/\s+/)[0] || "there";
  const carrier = row?.carrier ? ` ${row.carrier}` : "";
  const event = String(row?.source_event_type || "").toLowerCase();
  const type = String(row?.followup_type || "").toLowerCase();
  let reason = `I'm following up regarding your${carrier} insurance policy.`;
  if (event === "nsf" || event === "returned_eft" || type.includes("nsf")) {
    reason = `I'm following up regarding an important service item on your${carrier} insurance policy.`;
  } else if (event === "first_reminder") {
    reason = `I'm following up regarding a policy service reminder from${carrier || " your carrier"}.`;
  } else if (type.includes("policy receipt") || event === "issued_inforce") {
    reason = `I'm checking in to make sure you received your${carrier} policy and have what you need.`;
  } else if (type.includes("annual") || type.includes("6-month") || type.includes("90-day") || type.includes("30-day")) {
    reason = `I'm checking in regarding your${carrier} policy.`;
  }
  return `Hi ${first}, this is ${agentName}. ${reason} Please call or text me when convenient. Reply STOP to opt out.`;
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-foreground">{value || "—"}</div>
    </div>
  );
}

export default function FollowUpNowDialog({ row, open, onOpenChange, onUpdated }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [facts, setFacts] = useState(null);
  const [smsState, setSmsState] = useState(null);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [message, setMessage] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [outcome, setOutcome] = useState("");
  const [permissionBasis, setPermissionBasis] = useState("");
  const [permissionBusy, setPermissionBusy] = useState(false);
  const [phoneCorrection, setPhoneCorrection] = useState("");
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [emailCorrection, setEmailCorrection] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [identities, setIdentities] = useState([]);
  const [selectedIdentityId, setSelectedIdentityId] = useState("");
  const [sophiaOpen, setSophiaOpen] = useState(false);
  const [sophiaPreflight, setSophiaPreflight] = useState(null);
  const [sophiaPreflightLoading, setSophiaPreflightLoading] = useState(false);
  const [sophiaLaunching, setSophiaLaunching] = useState(false);
  const [sophiaIdemKey, setSophiaIdemKey] = useState("");
  const [sophiaResult, setSophiaResult] = useState(null);
  const [sophiaTestMode, setSophiaTestMode] = useState(false);
  const [queueHold, setQueueHold] = useState(false);
  const [queueExcluded, setQueueExcluded] = useState(false);
  const [queueReview, setQueueReview] = useState(false);
  const [queueSnoozeUntil, setQueueSnoozeUntil] = useState("");
  const [snoozeInput, setSnoozeInput] = useState("");
  const [queueBusy, setQueueBusy] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    let alive = true;
    const load = async () => {
      setLoading(true);
      setLead(null);
      setFacts(null);
      setSmsState(null);
      setProvider(null);
      setDueDate(row.due_date || "");
      setOutcome("");
      setEmailSubject("");
      setEmailBody("");
      setPhoneCorrection("");
      setEmailCorrection("");
      setSophiaOpen(false);
      setSophiaPreflight(null);
      setSophiaResult(null);
      setSophiaTestMode(false);
      setSelectedIdentityId("");
      setQueueHold(Boolean(row.sophia_hold));
      setQueueExcluded(Boolean(row.sophia_excluded));
      setQueueReview(Boolean(row.sophia_human_review_required));
      setQueueSnoozeUntil(row.sophia_snooze_until || "");
      setSnoozeInput(row.sophia_snooze_until || "");
      try {
        const [factsRes, providerRes, identitiesRes] = await Promise.all([
          api.get(`/voice-ai/auto/followups/${row.id}/facts`),
          api.get("/sms/provider-status").catch(() => ({ data: null })),
          api.get("/communication-identities", { params: { usable_only: true } }).catch(() => ({ data: [] })),
        ]);
        if (alive) setIdentities(identitiesRes?.data || []);
        if (!alive) return;
        const data = factsRes.data;
        const linkedId = data.lead_id || data.client_id;
        const linked = linkedId ? {
          id: linkedId,
          client_name: data.client_name,
          phone: data.phone,
          email: data.email,
        } : null;
        setFacts(data);
        setLead(linked);
        setProvider(providerRes?.data || null);
        setMessage(suggestedText({
          ...row,
          client_name: data.client_name || row.client_name,
          carrier: data.carrier || row.carrier,
          followup_type: data.triaged_type || row.effective_followup_type || row.followup_type,
          source_event_type: data.event_type || row.source_event_type,
        }, linked, user?.name || "your agent"));
        if (data.lead_id) {
          try {
            const { data: smsData } = await api.get(`/sms/leads/${data.lead_id}`);
            if (alive) setSmsState(smsData);
          } catch (_) {
            if (alive) setSmsState(null);
          }
        }
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load follow-up context");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [open, row, user?.name]);

  const email = facts?.email || row?.resolved_email || row?.contact?.email || "";
  const resolvedLeadId = facts?.lead_id || row?.lead_id || "";
  const resolvedClientId = facts?.client_id || row?.client_id || "";
  const canonicalContactId = facts?.lead_id || facts?.client_id || row?.lead_id || row?.client_id || "";
  const displayPhone = facts?.phone || row?.resolved_phone || row?.contact?.phone || "";
  const displayCarrier = facts?.carrier || row?.carrier || row?.policy?.carrier || "";
  const displayAssigned = facts?.assigned_to || row?.assigned_to || "";
  const effectiveFollowupType = facts?.triaged_type || row?.effective_followup_type || row?.followup_type || "Follow-Up";
  const effectiveServiceGoal = facts?.service_goal || row?.next_action || "Review the record and contact the client as appropriate.";
  const verifiedPolicyStatus = facts?.verified_status || row?.verified_carrier_status || row?.policy?.carrier_status || "Not verified";
  const verifiedPaymentStatus = facts?.verified_payment_status || row?.verified_payment_status || row?.policy?.payment_status || "Not verified";
  const noOutbound = Boolean(row?.no_outbound || row?.outreach_suppressed || facts?.suppressed || smsState?.dnc || smsState?.opt_out);
  const voiceRoleAllowed = ["admin", "agent", "bpo_closer"].includes(user?.role);
  const canManageAiPermission = ["admin", "ops_manager"].includes(user?.role);
  const phoneCorrectionGate = canonicalPhoneCorrectionGate(facts, user?.role);
  const callGate = canonicalCallGate(facts, user?.role);
  const canCorrectPhone = ["admin", "ops_manager", "agent"].includes(user?.role);
  const callAllowed = Boolean(callGate.allowed && !noOutbound);
  const smsConsent = Boolean(smsState?.sms_opt_in && smsState?.consent_sms_permission);
  const smsConfigured = provider?.configured === true;
  const textAllowed = Boolean(resolvedLeadId && displayPhone && !noOutbound && smsConsent && smsConfigured);
  const emailGate = canonicalEmailGate(facts);
  const emailAllowed = emailGate.allowed;

  const blockReason = useMemo(() => {
    if (noOutbound) return "Outbound contact is blocked by DNC, opt-out, or a no-contact instruction.";
    if (!canonicalContactId) return "No linked contact could be resolved for this follow-up.";
    if (!displayPhone) return "The linked contact does not have a callable phone number.";
    if (!voiceRoleAllowed) return "Calling from Fortunate Ops is available to administrators, licensed agents, and assigned BPO callers.";
    if (facts?.callable !== true) return facts?.missing_reason || "This linked contact is not callable.";
    return "";
  }, [noOutbound, canonicalContactId, displayPhone, voiceRoleAllowed, facts?.callable, facts?.missing_reason]);

  const textReason = useMemo(() => {
    if (noOutbound) return "Outbound contact is blocked by DNC, opt-out, or a no-contact instruction.";
    if (!canonicalContactId) return "No linked contact could be resolved for this follow-up.";
    if (!displayPhone) return "The linked contact does not have a textable phone number.";
    if (!resolvedLeadId) return "This follow-up is linked to a client record. Texting still requires a linked lead with affirmative SMS permission.";
    if (!smsConsent) return "Texting is blocked until affirmative SMS permission is stored for this lead.";
    if (!smsConfigured) return "SMS is not configured for this agency yet.";
    return "";
  }, [noOutbound, canonicalContactId, resolvedLeadId, displayPhone, smsConsent, smsConfigured]);

  const emailReason = useMemo(() => {
    if (noOutbound) return "Outbound contact is blocked by DNC, opt-out, or a no-contact instruction.";
    return emailGate.reason;
  }, [emailGate.reason, noOutbound]);

  const savePhoneCorrection = async () => {
    if (!phoneCorrectionGate.allowed || !phoneCorrection.trim() || !row?.id) return;
    setPhoneBusy(true);
    try {
      await api.put(`/followups/${row.id}/contact-phone`, { phone: phoneCorrection.trim() });
      const { data } = await api.get(`/voice-ai/auto/followups/${row.id}/facts`);
      setFacts(data);
      const linkedId = data.lead_id || data.client_id;
      setLead(linkedId ? {
        id: linkedId,
        client_name: data.client_name,
        phone: data.phone,
        email: data.email,
      } : null);
      setPhoneCorrection("");
      toast.success("Phone updated on the linked contact and follow-up context refreshed");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the linked contact phone");
    } finally {
      setPhoneBusy(false);
    }
  };

  const saveEmailCorrection = async () => {
    if (!canCorrectPhone || !emailCorrection.trim() || !row?.id) return;
    setEmailBusy(true);
    try {
      await api.put(`/followups/${row.id}/contact-email`, { email: emailCorrection.trim() });
      const { data } = await api.get(`/voice-ai/auto/followups/${row.id}/facts`);
      setFacts(data);
      const linkedId = data.lead_id || data.client_id;
      setLead(linkedId ? {
        id: linkedId,
        client_name: data.client_name,
        phone: data.phone,
        email: data.email,
      } : null);
      setEmailCorrection("");
      toast.success("Email added to the linked contact and follow-up context refreshed");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the linked contact email");
    } finally {
      setEmailBusy(false);
    }
  };

  const grantAiPermissionForThisFollowup = async () => {
    if (!canManageAiPermission || !facts || permissionBasis.trim().length < 12) return;
    setPermissionBusy(true);
    try {
      await api.post("/voice-ai/eligibility-grant", {
        lead_id: facts.lead_id || row?.lead_id || null,
        policy_id: facts.policy_id || row?.policy_id || null,
        policy_number: facts.policy_number || row?.policy_number || null,
        basis: permissionBasis.trim(),
        revoke: false,
      });
      const { data } = await api.get(`/voice-ai/auto/followups/${row.id}/facts`);
      setFacts(data);
      toast.success("AI voice authorization recorded for this follow-up");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not record AI voice authorization");
    } finally {
      setPermissionBusy(false);
    }
  };

  const generateAiDraft = async () => {
    if (!row?.id || noOutbound) return;
    setAiDrafting(true);
    try {
      const { data } = await api.post(`/followup-ai/followups/${row.id}/draft`, {
        communication_identity_id: selectedIdentityId || "",
      });
      setMessage(data.sms || message);
      setEmailSubject(data.email_subject || "");
      setEmailBody(data.email_body || "");
      toast.success("AI drafted the text and email. Review before sending.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI could not prepare the outreach drafts");
    } finally {
      setAiDrafting(false);
    }
  };

  const callNow = () => {
    if (!callAllowed) return;
    const params = new URLSearchParams({
      followup: String(row.id),
      to: displayPhone,
    });
    if (resolvedLeadId) params.set("lead", resolvedLeadId);
    else if (resolvedClientId) params.set("client", resolvedClientId);
    onOpenChange(false);
    navigate(`/telephony?${params.toString()}`);
  };

  // ---------------------------------------------------------- Sophia Makes the Call
  // Exact-ID only: every request below carries row.id explicitly. Nothing here ever
  // reads the queue, the current filter/sort, or any other follow-up's id.

  const loadSophiaPreflight = async (identityId) => {
    if (!row?.id) return;
    setSophiaPreflightLoading(true);
    try {
      const { data } = await api.post(`/voice-ai/auto/followups/${row.id}/preflight`, {
        communication_identity_id: identityId || null,
      });
      setSophiaPreflight(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load the Sophia preflight checklist");
      setSophiaPreflight(null);
    } finally {
      setSophiaPreflightLoading(false);
    }
  };

  const openSophiaPanel = () => {
    setSophiaOpen(true);
    setSophiaResult(null);
    setSophiaIdemKey(newIdempotencyKey());
    loadSophiaPreflight(selectedIdentityId);
  };

  const onSelectIdentity = (identityId) => {
    setSelectedIdentityId(identityId);
    if (sophiaOpen) loadSophiaPreflight(identityId);
  };

  const selectedIdentity = identities.find((x) => x.id === selectedIdentityId) || null;
  const sophiaOpenGate = canonicalSophiaOpenGate(facts, user?.role);
  const sophiaGate = canonicalSophiaCallGate(facts, user?.role, selectedIdentity);
  const sophiaPreflightAllowed = Boolean(sophiaPreflight?.allowed);

  const confirmSophiaCall = async () => {
    if (!row?.id || !sophiaPreflightAllowed || !selectedIdentityId || sophiaLaunching) return;
    setSophiaLaunching(true);
    try {
      const { data } = await api.post(`/voice-ai/auto/followups/${row.id}/call-now`, {
        communication_identity_id: selectedIdentityId,
        confirm: true,
        idempotency_key: sophiaIdemKey,
        test_mode: sophiaTestMode,
      });
      setSophiaResult(data);
      if (data.status === "dialing") {
        toast.success(`${data.test_mode ? "TEST MODE: " : ""}Sophia is calling ${facts?.client_name || "this client"} now.`);
        onUpdated?.();
      } else if (data.status === "duplicate") {
        toast.message("A Sophia call for this follow-up is already in progress.");
      } else {
        toast.error((data.blocking_reasons || [])[0] || "Sophia could not place this call");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sophia could not place this call");
    } finally {
      setSophiaLaunching(false);
    }
  };

  // ---------------------------------------------------------- Queue controls (K)
  // Pulls this exact record out of Sophia's automatic AND manual calling
  // without deleting it. Never a side effect of any other action here.

  const toggleHold = async () => {
    if (!row?.id) return;
    setQueueBusy(true);
    try {
      if (queueHold) {
        await api.post(`/voice-ai/auto/followups/${row.id}/unhold`);
        setQueueHold(false);
        toast.success("Hold removed — eligible for Sophia calling again");
      } else {
        await api.post(`/voice-ai/auto/followups/${row.id}/hold`, { reason: "" });
        setQueueHold(true);
        toast.success("Held from Sophia calling");
      }
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update hold status");
    } finally {
      setQueueBusy(false);
    }
  };

  const toggleExclude = async () => {
    if (!row?.id) return;
    setQueueBusy(true);
    try {
      await api.post(`/voice-ai/auto/followups/${row.id}/exclude`, { excluded: !queueExcluded });
      setQueueExcluded((v) => !v);
      toast.success(queueExcluded ? "Included back in Sophia campaigns/runs" : "Excluded from Sophia campaigns/runs");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update exclusion status");
    } finally {
      setQueueBusy(false);
    }
  };

  const toggleReview = async () => {
    if (!row?.id) return;
    setQueueBusy(true);
    try {
      await api.post(`/voice-ai/auto/followups/${row.id}/require-review`, { required: !queueReview });
      setQueueReview((v) => !v);
      toast.success(queueReview ? "Human-review requirement cleared" : "Flagged as requiring human review before Sophia calls");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update review requirement");
    } finally {
      setQueueBusy(false);
    }
  };

  const applySnooze = async () => {
    if (!row?.id || !snoozeInput) return;
    setQueueBusy(true);
    try {
      await api.post(`/voice-ai/auto/followups/${row.id}/snooze`, { until: snoozeInput });
      setQueueSnoozeUntil(snoozeInput);
      toast.success(`Snoozed until ${snoozeInput}`);
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not snooze this follow-up");
    } finally {
      setQueueBusy(false);
    }
  };

  const sendText = async () => {
    if (!textAllowed || !message.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sms/leads/${resolvedLeadId}/send`, { body: message.trim() });
      if (!data?.sent) {
        toast.error(`Text not sent: ${data?.reason || "provider unavailable"}`);
        return;
      }
      await api.put(`/followups/${row.id}`, {
        status_state: "in_progress",
        last_contact_date: new Date().toISOString().slice(0, 10),
        last_contact_result: "SMS sent",
      });
      toast.success("Follow-up text sent and logged");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Text could not be sent");
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    if (!emailAllowed || !emailSubject.trim() || !emailBody.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/followup-ai/followups/${row.id}/send-email`, {
        subject: emailSubject.trim(), body: emailBody.trim(),
      });
      if (!data?.sent) { toast.error("Email was not sent"); return; }
      toast.success("Follow-up email sent and logged");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Email could not be sent");
    } finally {
      setBusy(false);
    }
  };

  const reschedule = async () => {
    if (!dueDate) { toast.error("Choose a follow-up date"); return; }
    setBusy(true);
    try {
      await api.put(`/followups/${row.id}`, { due_date: dueDate, status_state: "rescheduled", last_contact_result: "Rescheduled" });
      toast.success("Follow-up rescheduled");
      onUpdated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reschedule follow-up");
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!outcome) { toast.error("Choose the outcome first"); return; }
    setBusy(true);
    try {
      const noAction = outcome === "No action needed";
      await api.put(`/followups/${row.id}`, {
        status_state: noAction ? "no_action_needed" : "completed",
        last_contact_date: new Date().toISOString().slice(0, 10),
        last_contact_result: outcome,
        next_action: "",
      });
      toast.success(noAction ? "Closed — no action needed" : "Follow-up completed");
      onUpdated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not complete follow-up");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="follow-up-now-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Follow Up Now</DialogTitle>
          <DialogDescription>Everything needed to act on this follow-up without leaving the workflow.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="grid min-h-56 place-items-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 rounded-lg border bg-secondary/30 p-4 sm:grid-cols-5">
              <Info label="Client" value={facts?.client_name || row?.client_name} />
              <Info label="Phone" value={displayPhone} />
              <Info label="Email" value={email} />
              <Info label="Carrier" value={displayCarrier} />
              <Info label="Policy" value={facts?.policy_number || row?.policy_number} />
              <Info label="Assigned To" value={displayAssigned} />
              <Info label="Carrier-Verified Policy Status" value={verifiedPolicyStatus} />
              <Info label="Carrier-Verified Payment / Draft Status" value={verifiedPaymentStatus} />
              <Info label="Follow-Up Purpose" value={effectiveFollowupType} />
              <Info label="Follow-Up Task Status" value={row?.status_state} />
            </div>

            {facts && (
              <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="follow-up-sophia-readiness">
                <span className="font-semibold uppercase tracking-wider text-muted-foreground">Sophia readiness:</span>
                {facts.readiness === "AI_READY" && <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 font-semibold text-emerald-800">AI Ready</span>}
                {facts.readiness === "URGENT_AI_READY" && <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 font-semibold text-emerald-900">Urgent · AI Ready</span>}
                {facts.readiness === "CARRIER_VERIFICATION_REQUIRED" && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-semibold text-amber-900">Carrier Verification Required</span>}
                {facts.readiness === "AGENT_REVIEW_REQUIRED" && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-semibold text-amber-900">Agent Review Required</span>}
                {facts.readiness === "AI_PERMISSION_REQUIRED" && <span className="rounded-full bg-violet-100 px-2.5 py-0.5 font-semibold text-violet-900">AI Permission Required</span>}
                {facts.readiness === "MISSING_CONTACT" && <span className="rounded-full bg-rose-100 px-2.5 py-0.5 font-semibold text-rose-800">Missing Phone</span>}
                {facts.readiness === "NO_OUTBOUND" && <span className="rounded-full bg-rose-100 px-2.5 py-0.5 font-semibold text-rose-800">No Outbound / DNC</span>}
                {facts.triaged_type && <span className="text-muted-foreground">→ <strong className="text-foreground">{facts.triaged_type}</strong></span>}
                {facts.conflict && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-800" title="Verified carrier status overrides the imported follow-up type">reclassified from “{facts.current_followup_type}”</span>}
                {facts.phone
                  ? <span className="text-muted-foreground">· phone <strong className="text-foreground">{facts.phone}</strong>{facts.phone_source ? ` (via ${facts.phone_source})` : ""}</span>
                  : <span className="text-rose-700">· {(facts.readiness_reasons || [])[0] || "No callable phone resolved"}</span>}
                {facts.reason && <span className="w-full text-[11px] text-muted-foreground">{facts.reason}</span>}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="follow-up-queue-controls">
              <span className="font-semibold uppercase tracking-wider text-muted-foreground">Queue:</span>
              <Button size="sm" variant={queueHold ? "destructive" : "outline"} disabled={queueBusy} onClick={toggleHold} data-testid="follow-up-toggle-hold">
                {queueHold ? "Remove Hold" : "Hold from Sophia"}
              </Button>
              <Button size="sm" variant={queueExcluded ? "destructive" : "outline"} disabled={queueBusy} onClick={toggleExclude} data-testid="follow-up-toggle-exclude">
                {queueExcluded ? "Include in Runs" : "Exclude from Runs"}
              </Button>
              <Button size="sm" variant={queueReview ? "destructive" : "outline"} disabled={queueBusy} onClick={toggleReview} data-testid="follow-up-toggle-review">
                {queueReview ? "Clear Review Flag" : "Require Human Review"}
              </Button>
              <Input type="date" className="h-8 w-36" value={snoozeInput} onChange={(e) => setSnoozeInput(e.target.value)} data-testid="follow-up-snooze-date" />
              <Button size="sm" variant="outline" disabled={queueBusy || !snoozeInput} onClick={applySnooze} data-testid="follow-up-apply-snooze">
                Snooze
              </Button>
              {queueSnoozeUntil && <span className="text-muted-foreground">Snoozed until {queueSnoozeUntil}</span>}
            </div>

            {facts && !facts.phone && canCorrectPhone && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-xs" data-testid="follow-up-phone-correction">
                <div className="font-semibold text-amber-950">Missing phone on the canonical linked contact</div>
                {canonicalContactId ? (
                  <>
                    <p className="mt-1 text-amber-900/80">Enter the verified client phone. It will be saved only on the exact linked {facts.lead_id ? "lead" : "client"} record and audited.</p>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                      <Input value={phoneCorrection} onChange={(e) => setPhoneCorrection(e.target.value)}
                        placeholder="10-digit phone number" inputMode="tel" data-testid="follow-up-phone-input" />
                      <Button variant="outline" onClick={savePhoneCorrection}
                        disabled={phoneBusy || !phoneCorrection.trim()} data-testid="follow-up-phone-save">
                        {phoneBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                        Save Verified Phone
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="mt-1 text-amber-900/80">This follow-up has no deterministic lead/client link yet. Verify the policy/application link before adding contact details.</p>
                )}
              </div>
            )}

            {facts && !email && canCorrectPhone && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-xs" data-testid="follow-up-email-correction">
                <div className="font-semibold text-amber-950">Missing email on the canonical linked contact</div>
                {canonicalContactId ? (
                  <>
                    <p className="mt-1 text-amber-900/80">Enter the verified client email. It will be saved only on the exact linked {facts.lead_id ? "lead" : "client"} record and audited.</p>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                      <Input value={emailCorrection} onChange={(e) => setEmailCorrection(e.target.value)}
                        placeholder="client@example.com" type="email" data-testid="follow-up-email-input" />
                      <Button variant="outline" onClick={saveEmailCorrection}
                        disabled={emailBusy || !emailCorrection.trim()} data-testid="follow-up-email-save">
                        {emailBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                        Save Verified Email
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="mt-1 text-amber-900/80">This follow-up has no deterministic lead/client link yet. Verify the policy/application link before adding contact details.</p>
                )}
              </div>
            )}

            {facts?.readiness === "AI_PERMISSION_REQUIRED" && canManageAiPermission && (
              <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 text-xs" data-testid="follow-up-ai-permission">
                <div className="font-semibold text-violet-950">AI voice authorization required for this record</div>
                <p className="mt-1 text-violet-900/80">Record the documented authorization basis for this client. This does not bypass DNC and does not start a call.</p>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <Input value={permissionBasis} onChange={(e) => setPermissionBasis(e.target.value)} placeholder="e.g. client consent / authorization reference" />
                  <Button variant="outline" onClick={grantAiPermissionForThisFollowup} disabled={permissionBusy || permissionBasis.trim().length < 12}>
                    {permissionBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                    Record Authorization
                  </Button>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-[#315A7D]/20 bg-[#315A7D]/5 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-[#315A7D]">Why this needs attention</div>
                  <div className="mt-1 font-medium">{effectiveFollowupType}</div>
                  <div className="mt-2 text-sm text-muted-foreground">{effectiveServiceGoal}</div>
                </div>
                <Button variant="outline" onClick={generateAiDraft} disabled={aiDrafting || noOutbound || !row?.id}
                  className="border-[#B28A3D]/40 bg-white" data-testid="follow-up-ai-draft">
                  {aiDrafting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4 text-[#B28A3D]" />}
                  Draft Text + Email with AI
                </Button>
              </div>
              <div className="mt-3">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Who this outreach discloses itself as
                </Label>
                <Select value={selectedIdentityId} onValueChange={onSelectIdentity}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="follow-up-outreach-identity">
                    <SelectValue placeholder="Use my own name (no verified identity selected)" />
                  </SelectTrigger>
                  <SelectContent>
                    {identities.length === 0 && (
                      <div className="px-2 py-1.5 text-muted-foreground">
                        No verified communication identity is configured for this agency yet.
                      </div>
                    )}
                    {identities.map((ident) => (
                      <SelectItem key={ident.id} value={ident.id}>
                        {ident.display_name} — {ident.agency_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">
                AI only drafts. You review and choose whether to send. Selecting a verified identity above applies to
                the text/email draft, the call identity, and Sophia alike — leave unselected to draft as yourself.
              </div>
            </div>

            {noOutbound && (
              <div className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="follow-up-now-blocked">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span><strong>No outbound contact.</strong> {blockReason}</span>
              </div>
            )}

            <div className="grid gap-3 lg:grid-cols-3">
              <div className="rounded-lg border p-4">
                <div className="mb-2 flex items-center gap-2 font-semibold"><PhoneCall className="h-4 w-4" /> Call</div>
                <p className="mb-3 text-xs text-muted-foreground">Choose who speaks. Sophia and a human agent are never interchangeable — this choice is explicit every time, for this exact follow-up only.</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Button onClick={callNow} disabled={!callAllowed || busy} data-testid="follow-up-call-now">
                    I&apos;ll Make the Call
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[#B28A3D]/40"
                    onClick={() => (sophiaOpen ? setSophiaOpen(false) : openSophiaPanel())}
                    disabled={!sophiaOpenGate.allowed}
                    data-testid="follow-up-sophia-call"
                  >
                    <Sparkles className="mr-1.5 h-4 w-4 text-[#B28A3D]" /> Sophia Makes the Call
                  </Button>
                </div>
                {!callAllowed && <p className="mt-2 text-[11px] text-amber-700">{blockReason}</p>}
                {!sophiaOpenGate.allowed && sophiaOpenGate.reasons[0] && (
                  <p className="mt-1 text-[11px] text-amber-700">{sophiaOpenGate.reasons[0]}</p>
                )}

                {sophiaOpen && (
                  <div className="mt-3 rounded-lg border border-[#B28A3D]/30 bg-[#B28A3D]/5 p-3 text-xs" data-testid="follow-up-sophia-preflight">
                    <div className="mb-2 font-semibold text-foreground">
                      Sophia preflight — {facts?.client_name || row?.client_name || "this client"}
                    </div>

                    <div className="mb-2">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Communication identity</Label>
                      <Select value={selectedIdentityId} onValueChange={onSelectIdentity}>
                        <SelectTrigger data-testid="follow-up-sophia-identity">
                          <SelectValue placeholder="Select a verified identity" />
                        </SelectTrigger>
                        <SelectContent>
                          {identities.length === 0 && (
                            <div className="px-2 py-1.5 text-muted-foreground">
                              No verified communication identity is configured for this agency yet.
                            </div>
                          )}
                          {identities.map((ident) => (
                            <SelectItem key={ident.id} value={ident.id}>
                              {ident.display_name} — {ident.agency_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {sophiaPreflightLoading ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking readiness…
                      </div>
                    ) : sophiaPreflight ? (
                      <div className="space-y-1.5">
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          <Info label="Phone" value={sophiaPreflight.phone} />
                          <Info label="Carrier" value={sophiaPreflight.carrier} />
                          <Info label="Policy / App Ref" value={sophiaPreflight.policy_reference} />
                          <Info label="Carrier-Verified Status" value={sophiaPreflight.carrier_verified_status} />
                          <Info label="Purpose" value={sophiaPreflight.follow_up_purpose} />
                          <Info label="Next Action" value={sophiaPreflight.next_action} />
                          <Info label="Assigned Agent" value={sophiaPreflight.assigned_agent} />
                          <Info label="Agency" value={sophiaPreflight.agency_name} />
                          <Info label="Callback Number" value={sophiaPreflight.callback_number} />
                          <Info label="Live-Transfer Destination" value={sophiaPreflight.live_transfer_destination} />
                          <Info label="Contact-Link Source" value={sophiaPreflight.contact_link_source} />
                          <Info label="AI Permission" value={sophiaPreflight.ai_permission_established ? "Established" : "Not established"} />
                          <Info label="DNC" value={sophiaPreflight.dnc ? "Yes" : "No"} />
                          <Info label="Opt-Out" value={sophiaPreflight.opt_out ? "Yes" : "No"} />
                          <Info label="Readiness" value={sophiaPreflight.readiness} />
                          <Info label="Agency Timezone" value={sophiaPreflight.agency_timezone} />
                          <Info label="Calling Hours (local)" value={sophiaPreflight.call_window
                            ? `${String(sophiaPreflight.call_window.start).padStart(2, "0")}:00–${String(sophiaPreflight.call_window.end).padStart(2, "0")}:00`
                            : "—"} />
                          <Info label="Within Calling Hours Now" value={sophiaPreflight.within_call_window ? "Yes" : "No — outside window"} />
                          <Info label="Recording / Disclosure" value={sophiaPreflight.recording_disclosure_required ? "Required — disclosed on the call" : "—"} />
                        </div>

                        {sophiaPreflight.blocking_reasons?.length > 0 && (
                          <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-2 text-rose-800" data-testid="follow-up-sophia-blocked">
                            <div className="font-semibold">Blocked:</div>
                            <ul className="ml-4 list-disc">
                              {sophiaPreflight.blocking_reasons.map((reason) => <li key={reason}>{reason}</li>)}
                            </ul>
                          </div>
                        )}

                        {sophiaResult && (
                          <div
                            className={`mt-2 rounded border p-2 ${sophiaResult.status === "dialing"
                              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                              : "border-amber-200 bg-amber-50 text-amber-800"}`}
                            data-testid="follow-up-sophia-result"
                          >
                            {sophiaResult.status === "dialing"
                              ? `${sophiaResult.test_mode ? "TEST MODE: " : ""}Sophia is calling now. Session ${sophiaResult.session_id}.`
                              : (sophiaResult.blocking_reasons || [])[0] || `Not dialed (${sophiaResult.status}).`}
                          </div>
                        )}

                        {sophiaResult?.status === "dialing" && sophiaResult.session_id && (
                          <SophiaLiveCallPanel sessionId={sophiaResult.session_id} />
                        )}

                        {["admin", "ops_manager"].includes(user?.role) && (
                          <label className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="follow-up-sophia-test-mode-toggle">
                            <input type="checkbox" checked={sophiaTestMode} onChange={(e) => setSophiaTestMode(e.target.checked)} />
                            <span>TEST MODE — same eligibility checks, limited daily cap, recorded separately. Only use on a record you've deliberately chosen for testing.</span>
                          </label>
                        )}
                        {sophiaTestMode && (
                          <div className="mt-1 rounded border border-amber-300 bg-amber-100 px-2 py-1 text-center text-[11px] font-semibold uppercase tracking-wider text-amber-900" data-testid="follow-up-sophia-test-mode-banner">
                            Test Mode
                          </div>
                        )}

                        <Button
                          className="mt-2 w-full"
                          onClick={confirmSophiaCall}
                          disabled={!sophiaPreflightAllowed || !selectedIdentityId || sophiaLaunching}
                          data-testid="follow-up-sophia-confirm"
                        >
                          {sophiaLaunching ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
                          {sophiaTestMode ? "Confirm — TEST MODE Sophia Call" : `Confirm — Sophia Calls ${facts?.client_name || "This Client"} Now`}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="rounded-lg border p-4">
                <div className="mb-2 flex items-center gap-2 font-semibold"><MessageSquareText className="h-4 w-4" /> Text</div>
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={480} rows={6} disabled={noOutbound} data-testid="follow-up-text-body" />
                <div className="mt-1 text-right text-[10px] text-muted-foreground">{message.length}/480</div>
                <Button className="mt-2 w-full" onClick={sendText} disabled={!textAllowed || busy || !message.trim()} data-testid="follow-up-text-now">Send Text Now</Button>
                {!textAllowed && <p className="mt-2 text-[11px] text-amber-700">{textReason}</p>}
              </div>

              <div className="rounded-lg border p-4">
                <div className="mb-2 flex items-center gap-2 font-semibold"><Mail className="h-4 w-4" /> Email</div>
                <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} maxLength={160}
                  placeholder="AI draft or enter subject" disabled={noOutbound} data-testid="follow-up-email-subject" />
                <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} maxLength={5000} rows={5}
                  className="mt-2" placeholder="AI draft or write the email" disabled={noOutbound} data-testid="follow-up-email-body" />
                <Button className="mt-2 w-full" onClick={sendEmail}
                  disabled={!emailAllowed || busy || !emailSubject.trim() || !emailBody.trim()} data-testid="follow-up-email-now">Send Email Now</Button>
                {!emailAllowed && <p className="mt-2 text-[11px] text-amber-700">{emailReason}</p>}
              </div>
            </div>

            <div className="grid gap-3 border-t pt-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Reschedule</Label>
                <div className="mt-1 flex gap-2">
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="follow-up-reschedule-date" />
                  <Button variant="outline" onClick={reschedule} disabled={busy || !dueDate} data-testid="follow-up-reschedule"><CalendarClock className="mr-1.5 h-4 w-4" /> Save</Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Complete / Close</Label>
                <div className="mt-1 flex gap-2">
                  <Select value={outcome} onValueChange={setOutcome}>
                    <SelectTrigger data-testid="follow-up-outcome"><SelectValue placeholder="Outcome" /></SelectTrigger>
                    <SelectContent>{COMPLETE_OUTCOMES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={complete} disabled={busy || !outcome} data-testid="follow-up-complete"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Done</Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
